import type { ReactNode } from "react";
import { ChevronLeft, Sparkles } from "lucide-react";
import { cn } from "@/lib/utils";

/** Onboarding palette (the flow's own warm tokens). */
export const OC = {
  bg: "#F5F0E8",
  text: "#2C1810",
  textSec: "#7A6553",
  muted: "#A0845A",
  cta: "#3D2210",
  ctaText: "#F5F0E8",
  tile: "#EDE6D8",
  divider: "#C8B89A",
  white: "#FFFFFF",
  track: "#E0D4C2",
  fill: "#3B2314",
  greyBg: "#E3D9CA",
  greyText: "#6B5747",
};

const SANS = "'DM Sans', sans-serif";

/** Back arrow and the one continuous progress bar. */
export function StepTopBar({ progress, onBack }: { progress: number | null; onBack?: () => void }) {
  return (
    <div className="relative flex items-center flex-shrink-0 mb-4" style={{ minHeight: 24 }} data-testid="onboarding-top-bar">
      {onBack ? (
        <button onClick={onBack} className="p-1 -ml-1 mr-2" aria-label="Go back" data-testid="onboarding-back">
          <ChevronLeft size={20} color={OC.textSec} />
        </button>
      ) : (
        <span className="w-[28px]" aria-hidden="true" />
      )}
      {progress !== null && (
        <div
          className="flex-1 mr-7 h-[3px] rounded-full overflow-hidden"
          style={{ backgroundColor: OC.track }}
          role="progressbar"
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={Math.round(progress * 100)}
          data-testid="onboarding-progress"
        >
          <div
            className="h-full rounded-full transition-[width] duration-300 ease-out"
            style={{ width: `${progress * 100}%`, backgroundColor: OC.fill }}
          />
        </div>
      )}
    </div>
  );
}

/** Small Groundy (60 px tall) beside the eyebrow. */
export function StepHeader({ groundy, eyebrow }: { groundy: string; eyebrow: string }) {
  return (
    <div className="flex items-end gap-2 mb-3">
      <img src={groundy} alt="" aria-hidden="true" className="h-[60px] w-auto object-contain" data-testid="step-groundy" />
      <span
        className="pb-1.5"
        style={{ fontFamily: SANS, textTransform: "uppercase", letterSpacing: "0.12em", fontSize: 11, fontWeight: 500, color: OC.muted }}
      >
        {eyebrow}
      </span>
    </div>
  );
}

export function StepHeading({ children, center = false }: { children: ReactNode; center?: boolean }) {
  return (
    <h1
      className={cn("text-[28px] leading-[1.2] text-balance", center ? "text-center" : "text-left")}
      style={{ fontFamily: "'Playfair Display', serif", fontWeight: 600, color: OC.text, letterSpacing: "normal" }}
    >
      {children}
    </h1>
  );
}

export function StepLine({ children, className, center = false }: { children: ReactNode; className?: string; center?: boolean }) {
  return (
    <p
      className={cn("mt-2 text-pretty", center ? "text-center" : "text-left", className)}
      style={{ fontFamily: SANS, fontSize: 14, lineHeight: 1.5, color: OC.textSec }}
    >
      {children}
    </p>
  );
}

/** The real For You tag, kept on one line. */
export function ForYouTag() {
  return (
    <span
      className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 align-middle whitespace-nowrap"
      style={{ background: "linear-gradient(135deg, #c4a882, #b8956e)" }}
      data-testid="for-you-tag"
    >
      <Sparkles className="h-3 w-3" color="#FFFFFF" />
      <span style={{ fontSize: 11, fontWeight: 700, color: "#FFFFFF", fontFamily: SANS }}>For You</span>
    </span>
  );
}

type ButtonKind = "primary" | "outline" | "dark" | "light";

/** Main buttons. A disabled primary uses the greyed colors. */
export function OButton({
  children,
  onClick,
  disabled = false,
  kind = "primary",
  type = "button",
  className,
  testId,
}: {
  children: ReactNode;
  onClick?: () => void;
  disabled?: boolean;
  kind?: ButtonKind;
  type?: "button" | "submit";
  className?: string;
  testId?: string;
}) {
  const styles: Record<ButtonKind, React.CSSProperties> = {
    primary: disabled
      ? { backgroundColor: OC.greyBg, color: OC.greyText }
      : { backgroundColor: OC.cta, color: OC.ctaText },
    outline: { backgroundColor: "transparent", color: OC.text, border: `1px solid ${OC.divider}` },
    dark: { backgroundColor: "#1A1A1A", color: OC.white },
    light: { backgroundColor: OC.white, color: OC.text, border: `0.5px solid ${OC.divider}` },
  };
  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      data-testid={testId}
      className={cn(
        "w-full h-[52px] rounded-[14px] flex items-center justify-center gap-2 transition-opacity",
        disabled ? "cursor-not-allowed" : "hover:opacity-90",
        className,
      )}
      style={{ ...styles[kind], fontFamily: SANS, fontSize: 15, fontWeight: 500 }}
    >
      {children}
    </button>
  );
}

export function TextLink({ children, onClick, testId }: { children: ReactNode; onClick: () => void; testId?: string }) {
  return (
    <button
      onClick={onClick}
      data-testid={testId}
      className="w-full text-center mt-3"
      style={{ fontFamily: SANS, fontSize: 13, color: "#7B4F2E", fontWeight: 500 }}
    >
      {children}
    </button>
  );
}

/**
 * Body that scrolls above a main button docked in the same place on every
 * screen. `fade` shows a short fade at the bottom of the scrolling part.
 */
export function ScreenFrame({ children, dock, fade = false, testId }: { children: ReactNode; dock: ReactNode; fade?: boolean; testId?: string }) {
  return (
    <div className="flex flex-col flex-1 min-h-0 w-full" data-testid={testId}>
      <div className="relative flex-1 min-h-0">
        <div className="h-full overflow-y-auto overflow-x-hidden pb-2" data-testid={testId ? `${testId}-body` : undefined}>
          {children}
        </div>
        {fade && (
          <div
            aria-hidden="true"
            data-testid="scroll-fade"
            className="pointer-events-none absolute inset-x-0 bottom-0 h-8"
            style={{ background: `linear-gradient(to bottom, rgba(245,240,232,0), ${OC.bg})` }}
          />
        )}
      </div>
      <div className="w-full flex-shrink-0 pt-3" data-testid="onboarding-dock">
        {dock}
      </div>
    </div>
  );
}

/** One line above a greyed out button that says what is missing. */
export function MissingLine({ children }: { children: ReactNode }) {
  return (
    <p className="text-center mb-2" style={{ fontFamily: SANS, fontSize: 13, color: OC.greyText }} data-testid="missing-line">
      {children}
    </p>
  );
}

export function TermsLine() {
  return (
    <p className="text-center mt-4" style={{ fontFamily: SANS, fontSize: 12, color: OC.textSec, lineHeight: 1.5 }}>
      By continuing, you agree to our{" "}
      <a href="/terms" className="underline" style={{ color: "#7B4F2E" }}>Terms of Service</a> and{" "}
      <a href="/privacy" className="underline" style={{ color: "#7B4F2E" }}>Privacy Policy</a>.
    </p>
  );
}
